import {
    normalizeOrganizationCourseUpdateEvent,
    normalizeOrganizationCourseUpdateEvents,
    buildUnviewedOrgCourseUpdateMaps,
    pickLatestUnviewedEventForCourse,
    normalizeOrganizationCourseId,
} from '@/utils/organizationCourseUpdateLog.js'

const baseLink = () => import.meta.env.VITE_BASE_LINK

function unwrapEvents (data) {
    if (data?.code != null && Number(data.code) !== 200) {
        const msg = data?.message ?? data?.error
        throw new Error(typeof msg === 'string' && msg.trim() ? msg.trim() : 'Request failed.')
    }
    return data
}

/**
 * @param {import('axios').AxiosInstance} axios
 */
export async function fetchOrganizationCourseUpdateLog (axios, {
    organizationId,
    courseId = null,
    limit = 50,
    offset = 0,
} = {}) {
    const { data } = await axios.post(`${baseLink()}/get-organization-course-update-log`, {
        organizationId,
        courseId: courseId || undefined,
        limit,
        offset,
    })
    unwrapEvents(data)
    return {
        events: normalizeOrganizationCourseUpdateEvents(data),
        total: Number(data?.total) || 0,
    }
}

/**
 * @param {import('axios').AxiosInstance} axios
 */
export async function fetchUnviewedOrganizationCourseUpdates (axios, {
    organizationId,
    instructorUserId,
    courseId = null,
} = {}) {
    const { data } = await axios.post(`${baseLink()}/get-unviewed-organization-course-updates-for-instructor`, {
        organizationId,
        instructorUserId,
        courseId: courseId || undefined,
    })
    unwrapEvents(data)

    const rawEvent = data?.event ?? data?.updateEvent ?? data?.update_event
    const event = rawEvent && typeof rawEvent === 'object'
        ? normalizeOrganizationCourseUpdateEvent(rawEvent)
        : null

    const list = data?.unviewed
        ?? data?.events
        ?? data?.updateEvents
        ?? data?.update_events
        ?? []
    const allUnviewed = Array.isArray(list)
        ? list.map(normalizeOrganizationCourseUpdateEvent).filter((e) => e.eventId)
        : []

    const normalizeId = (id) => normalizeOrganizationCourseId(id)

    if (courseId) {
        const courseKey = normalizeId(courseId)
        const forCourse = allUnviewed.filter(
            (event) => normalizeId(event.courseId) === courseKey
        )
        const latest = pickLatestUnviewedEventForCourse(forCourse, courseId, normalizeId)
            || (event?.eventId && normalizeId(event.courseId) === courseKey ? event : null)
        return {
            event: latest,
            unviewed: forCourse,
        }
    }

    const { eventMap } = buildUnviewedOrgCourseUpdateMaps(allUnviewed, { normalizeId })
    const collapsed = Object.values(eventMap)

    return {
        event: event?.eventId ? event : (collapsed[0] || null),
        unviewed: collapsed,
        allUnviewed,
    }
}

/**
 * @param {import('axios').AxiosInstance} axios
 */
export async function fetchOrganizationCourseUpdateEvent (axios, {
    organizationId,
    eventId,
} = {}) {
    const { data } = await axios.post(`${baseLink()}/get-organization-course-update-event`, {
        organizationId,
        eventId,
    })
    unwrapEvents(data)
    const raw = data?.event ?? data?.updateEvent ?? data?.update_event ?? data
    const event = normalizeOrganizationCourseUpdateEvent(raw)
    if (!event.eventId) {
        throw new Error('Update event not found.')
    }
    return event
}

/**
 * @param {import('axios').AxiosInstance} axios
 */
export async function markOrganizationCourseUpdateViewed (axios, {
    organizationId,
    instructorUserId,
    eventId,
    courseId = null,
    markAllForCourse = false,
} = {}) {
    const { data } = await axios.post(`${baseLink()}/mark-organization-course-update-viewed`, {
        organizationId,
        instructorUserId,
        eventId,
        courseId: courseId || undefined,
        markAllForCourse: markAllForCourse || undefined,
    })
    unwrapEvents(data)
    return true
}

/**
 * @param {{ unviewedCourseUpdateCountsByCourseId?: Record<string, number>, courses?: Array<{ id: string, unviewedCourseUpdateCount?: number }> }} payload
 */
export function buildUnviewedCountsByCourseId (payload = {}) {
    const map = {}
    const top = payload.unviewedCourseUpdateCountsByCourseId
        ?? payload.unviewed_course_update_counts_by_course_id
    if (top && typeof top === 'object') {
        Object.entries(top).forEach(([courseId, count]) => {
            const n = Number(count) || 0
            if (n > 0) map[String(courseId)] = n
        })
    }
    const courses = payload.courses
    if (Array.isArray(courses)) {
        courses.forEach((course) => {
            const id = course?.id
            if (id == null) return
            const n = Number(course.unviewedCourseUpdateCount ?? course.unviewed_course_update_count) || 0
            if (n > 0) map[String(id)] = n
        })
    }
    return map
}
